// ============================================================================
// 정통 한국장기 18급 ~ 9단 승강제 티어 시스템 & 3-버킷 원장(Ledger) · 통합 카탈로그 경제 엔진
// - 실제 장기 급수(18급~1급)와 단수(1단~9단) 총 27단계 티어 및 승급전/강등방어전
// - 급수가 더 높은 유저와 매칭된 유저에게 기본 1개 ~ 최대 5개까지 '상수(象手)' 매칭 보정(matchGrant) 제공
// - 3-버킷 분리 원장:
//   1. matchGrant (대국당 급수차 보정 지급분 - 해당 대국 우선 소진)
//   2. dailyAllowance (일일 무료 충전분 - KST 자정 기준 리필)
//   3. purchasedInventory (유료/보석 구매분 - 일일 리셋으로 절대 소멸되지 않음)
// - 통합 SKU 카탈로그 (CatalogService): 상점 · 대국 중 즉시 충전 · 미녀대국 재도전 가격 100% 단일화
// - 멱등(Idempotent) 구버전 프로필 마이그레이션 (migrationVersion = 1)
// ============================================================================

export interface TierSpec {
  index: number;       // 0 (18급) .. 26 (9단)
  label: string;       // '18급' .. '1급', '1단' .. '9단'
  shortTitle: string;  // '동패 입문', '옥패 초단', '천하 명인' 등
  badgeAsset: string;
  baseRating: number;
  promotionWinsNeeded: number;
  promotionGamesTotal: number;
  aiDepth: number;
  aiRandomness: number; // 0 (perfect) .. 1 (beginner mistake rate)
}

export const ALL_TIERS: TierSpec[] = (() => {
  const list: TierSpec[] = [];
  // 18급 (index 0) .. 1급 (index 17)
  for (let kyu = 18; kyu >= 1; kyu--) {
    const idx = 18 - kyu;
    let badge = '/assets/images/tiers/tier_01_bronze_low.svg';
    let title = '동패 입문';
    if (kyu <= 14 && kyu >= 10) {
      badge = '/assets/images/tiers/tier_02_bronze_high.svg';
      title = '동패 수련';
    } else if (kyu <= 9 && kyu >= 7) {
      badge = '/assets/images/tiers/tier_03_silver_low.svg';
      title = '은패 중수';
    } else if (kyu <= 6 && kyu >= 5) {
      badge = '/assets/images/tiers/tier_04_silver_high.svg';
      title = '은패 숙련';
    } else if (kyu <= 4 && kyu >= 3) {
      badge = '/assets/images/tiers/tier_05_gold_low.svg';
      title = '금패 고수';
    } else if (kyu <= 2) {
      badge = '/assets/images/tiers/tier_06_gold_high.svg';
      title = '금패 정상';
    }
    const isHighKyu = kyu <= 9;
    list.push({
      index: idx,
      label: `${kyu}급`,
      shortTitle: title,
      badgeAsset: badge,
      baseRating: 800 + idx * 75,
      promotionWinsNeeded: isHighKyu ? 3 : 2,
      promotionGamesTotal: isHighKyu ? 5 : 3,
      aiDepth: kyu >= 13 ? 2 : kyu >= 6 ? 2 : 3,
      aiRandomness: Math.max(0.06, (kyu / 18) * 0.5)
    });
  }

  // 1단 (index 18) .. 9단 (index 26)
  for (let dan = 1; dan <= 9; dan++) {
    const idx = 17 + dan;
    let badge = '/assets/images/tiers/tier_07_jade_1_2dan.svg';
    let title = '옥패 유단자';
    if (dan >= 3 && dan <= 4) {
      badge = '/assets/images/tiers/tier_08_jade_3_4dan.svg';
      title = '옥패 중단자';
    } else if (dan >= 5 && dan <= 6) {
      badge = '/assets/images/tiers/tier_09_dragon_5_6dan.svg';
      title = '용패 고단자';
    } else if (dan >= 7 && dan <= 8) {
      badge = '/assets/images/tiers/tier_10_dragon_7_8dan.svg';
      title = '용패 대국수';
    } else if (dan === 9) {
      badge = '/assets/images/tiers/tier_11_master_9dan.svg';
      title = '천하 명인(名人)';
    }
    list.push({
      index: idx,
      label: `${dan}단`,
      shortTitle: title,
      badgeAsset: badge,
      baseRating: 2150 + (dan - 1) * 120,
      promotionWinsNeeded: dan >= 5 ? 4 : 3,
      promotionGamesTotal: dan >= 5 ? 7 : 5,
      aiDepth: 3,
      aiRandomness: Math.max(0, (9 - dan) * 0.015)
    });
  }
  return list;
})();

export function getTierByIndex(index: number): TierSpec {
  const clamped = Math.max(0, Math.min(ALL_TIERS.length - 1, Math.round(index)));
  return ALL_TIERS[clamped];
}

export function getTierByLabel(label: string): TierSpec {
  return ALL_TIERS.find(t => t.label === label) || ALL_TIERS[9]; // default 9급
}

// ============================================================================
// 상위 급수 유저와 매칭 시 기본 1개 ~ 최대 5개 '상수' 보정 지급 + 하수 메리트 '위기감지 방지권' 지급
// ============================================================================
export function calculateMatchHandicapSangsu(myTierIndex: number, opponentTierIndex: number): number {
  const diff = opponentTierIndex - myTierIndex;
  if (diff <= 0) return 0;
  return Math.min(5, Math.max(1, diff));
}

export function calculateMatchHandicapCrisisShields(myTierIndex: number, opponentTierIndex: number): number {
  const diff = opponentTierIndex - myTierIndex;
  if (diff <= 0) return 0;
  return diff >= 4 ? 2 : 1;
}

// ============================================================================
// KST(Asia/Seoul) 날짜 문자열 헬퍼 (일일 무료 충전 기준 시각)
// ============================================================================
export function getKstDateString(date: Date = new Date()): string {
  const kstMs = date.getTime() + 9 * 60 * 60 * 1000;
  return new Date(kstMs).toISOString().slice(0, 10);
}

// ============================================================================
// 경제 상수 & 통합 SKU 카탈로그 (CatalogService - A13)
// ============================================================================
export const ECONOMY_CONFIG = {
  FREE_DAILY_SANGSU_REFILL: 2,          // 무료 유저 일일 상수 2회 리필
  SUBSCRIPTION_DAILY_SANGSU_REFILL: 5,  // 유료 정액제 일일 상수 5회 리필 (급수 무관)
  FREE_DAILY_HUNSU_REFILL: 2,           // 무료 유저 일일 훈수 2회 기본 제공
  SUBSCRIPTION_DAILY_HUNSU_REFILL: 5,   // 유료 정액제 일일 훈수 5회 리필
  FREE_DAILY_CRISIS_SHIELD_REFILL: 1,   // 무료 유저 일일 위기감지 방지권 1개 기본 리필
  SUBSCRIPTION_DAILY_CRISIS_SHIELD_REFILL: 3, // 정액제 일일 위기감지 방지권 3개 리필
  FREE_DAILY_UNDO_REFILL: 1,            // 무료 유저 일일 한수 무르기 1개 리필
  SUBSCRIPTION_DAILY_UNDO_REFILL: 3,    // 정액제 일일 한수 무르기 3개 리필
  FREE_DAILY_ENEMY_INTENT_REFILL: 1,    // 무료 유저 일일 천안통 노림수 투시경 1개 리필
  SUBSCRIPTION_DAILY_ENEMY_INTENT_REFILL: 3, // 정액제 일일 천안통 3개 리필
  FREE_DAILY_REVIEW_LIMIT: 1,           // 무료 유저 복기 하루 1번
  SUBSCRIPTION_DAILY_REVIEW_LIMIT: 3,   // 정액제 유저 복기 하루 3번
  FREE_DAILY_CHALLENGE_TICKETS: 5,      // 무료 유저 일일 미녀대국 도전권 5장
  SUBSCRIPTION_DAILY_CHALLENGE_TICKETS: 10, // 정액제 일일 미녀대국 도전권 10장
  MAX_TACTICAL_USES_PER_GAME: 10,       // 한 게임에 상수/훈수 최대 10번까지 사용 가능
  MAX_CRISIS_SHIELDS_PER_GAME: 3,       // 한 판당 위기감지 방지권 최대 3개까지 사용 가능

  // 통합 SKU 카탈로그 기준 가격 (상점 · 인게임 즉시충전 · 미녀대국 재도전 단일화)
  PRICE_HUNSU_PACK3_GEMS: 60,           // sku_hunsu_3: 훈수 3회권 = 60보석
  PRICE_HUNSU_SINGLE_GEMS: 25,          // 단품 환산 기준
  PRICE_SANGSU_PACK4_GEMS: 40,          // sku_sangsu_4: 상수 4회권 = 40보석
  PRICE_SANGSU_PACK5_GEMS: 40,          // 하위호환 별칭
  PRICE_SANGSU_SINGLE_GEMS: 15,
  PRICE_CRISIS_SHIELD_GEMS: 45,         // sku_shield_1: 위기 감지 1회권 = 45보석
  PRICE_ENEMY_INTENT_PACK3_GEMS: 55,    // sku_vision_3: 천안통 3회권 = 55보석
  PRICE_UNDO_MOVE_PACK3_GEMS: 50,       // sku_undo_3: 무르기 3회권 = 50보석
  PRICE_RANK_SHIELD_PACK2_GEMS: 65,     // sku_ranked_shield_2: 점수 보호권 2회 = 65보석
  PRICE_CHALLENGE_TICKET_PACK5_GEMS: 90,// sku_ticket_5: 미녀대국 도전권 5장 = 90보석
  PRICE_PUZZLE_EXTEND_3MOVES_GEMS: 120, // sku_extend_3: 미녀대국 +3수 연장 = 120보석
  PRICE_EXTRA_REVIEW_GEMS: 40,          // 일일 복기 횟수 초과 시 추가 열람
  PRICE_SUBSCRIPTION_GEMS: 490,         // 천하명인 패스 보석 구매가
  PRICE_SUBSCRIPTION_KRW: 9900          // 천하명인 월 정액제
};

export type ConsumableItemType =
  | 'hunsu'
  | 'sangsu'
  | 'shield'
  | 'vision'
  | 'undo'
  | 'rankedShield'
  | 'ticket'
  | 'extend3Moves';

export type InventoryBucketName = 'matchGrant' | 'dailyAllowance' | 'purchasedInventory';

export type InventoryCounts = Record<ConsumableItemType, number>;

export interface InventoryBuckets {
  dailyAllowance: InventoryCounts;
  purchasedInventory: InventoryCounts;
  matchGrant: InventoryCounts;
}

export interface InventoryDeductionReceipt {
  receiptId: string;
  itemType: ConsumableItemType;
  amount: number;
  breakdown: Array<{ bucket: InventoryBucketName; amount: number }>;
  fromMatchGrant: number;
  fromDailyAllowance: number;
  fromPurchasedInventory: number;
  timestamp: number;
}

export interface CatalogSkuSpec {
  skuId: string;
  itemType: ConsumableItemType | 'vip';
  name: string;
  shortLabel: string;
  quantity: number;
  priceGems: number;
  priceCoins: number;
  badgeText: string;
  description: string;
  usageContext: string;
}

export const CATALOG_SKUS: Record<string, CatalogSkuSpec> = {
  sku_hunsu_3: {
    skuId: 'sku_hunsu_3',
    itemType: 'hunsu',
    name: '훈수 3회권',
    shortLabel: '훈수 +3',
    quantity: 3,
    priceGems: 60,
    priceCoins: 0,
    badgeText: '핵심 추천수',
    description: '전술적 이득이 검증된 구간에서만 열리는 훈수 3회 충전권입니다.',
    usageContext: '일반 대국 · 친구 대국 · 미녀대국 · 정답찾기'
  },
  sku_sangsu_4: {
    skuId: 'sku_sangsu_4',
    itemType: 'sangsu',
    name: '상수 4회권',
    shortLabel: '상수 +4',
    quantity: 4,
    priceGems: 40,
    priceCoins: 0,
    badgeText: '행마 경로 투시',
    description: '상(象)의 3턴 이동 경로와 포획 타겟을 한눈에 보여주는 상수 4회권입니다.',
    usageContext: '일반 대국 · 친구 대국 · 혼자 두기'
  },
  sku_shield_1: {
    skuId: 'sku_shield_1',
    itemType: 'shield',
    name: '위기 감지 1회권',
    shortLabel: '방지권 +1',
    quantity: 1,
    priceGems: 45,
    priceCoins: 0,
    badgeText: '안전 대안수 열람',
    description: '경고 알림 자체는 무료이며, 안전한 대안수를 열어볼 때만 1회 차감됩니다.',
    usageContext: '한 판 최대 3회 사용 가능'
  },
  sku_vision_3: {
    skuId: 'sku_vision_3',
    itemType: 'vision',
    name: '천안통 3회권',
    shortLabel: '천안통 +3',
    quantity: 3,
    priceGems: 55,
    priceCoins: 0,
    badgeText: '상대 노림수 투시',
    description: '상대의 다음 2수 핵심 위협 경로와 현재 형세를 분석합니다.',
    usageContext: '일반 대국 · 혼자 두기'
  },
  sku_undo_3: {
    skuId: 'sku_undo_3',
    itemType: 'undo',
    name: '무르기 3회권',
    shortLabel: '무르기 +3',
    quantity: 3,
    priceGems: 50,
    priceCoins: 0,
    badgeText: '혼자 두기 전용',
    description: 'AI 대국, 미녀대국, 정답찾기에서 방금 둔 수를 물릴 수 있습니다.',
    usageContext: '혼자 두기 · 미녀대국 · 정답찾기'
  },
  sku_ranked_shield_2: {
    skuId: 'sku_ranked_shield_2',
    itemType: 'rankedShield',
    name: '점수 보호권 2회',
    shortLabel: '보호권 +2',
    quantity: 2,
    priceGems: 65,
    priceCoins: 0,
    badgeText: '급·단 점수 방어',
    description: '패배 시 급·단 점수 하락을 막아주며, 상위 고수 격파 시 승점을 2배로 높여줍니다.',
    usageContext: '일반 대국 · 승급전 · 방어전'
  },
  sku_ticket_5: {
    skuId: 'sku_ticket_5',
    itemType: 'ticket',
    name: '미녀대국 도전권 5장',
    shortLabel: '도전권 +5',
    quantity: 5,
    priceGems: 90,
    priceCoins: 0,
    badgeText: '관문 도전 티켓',
    description: '미녀대국 20인의 관문에 즉시 도전할 수 있는 티켓 5장 묶음입니다.',
    usageContext: '미녀대국 1~20장 전 관문'
  },
  sku_extend_3: {
    skuId: 'sku_extend_3',
    itemType: 'extend3Moves',
    name: '미녀대국 +3수 연장',
    shortLabel: '+3수 연장',
    quantity: 1,
    priceGems: 120,
    priceCoins: 0,
    badgeText: '제한 수순 연장',
    description: '미녀대국 또는 정답찾기에서 제한 수순이 다했을 때 즉시 +3수를 연장합니다.',
    usageContext: '미녀대국 · 정답찾기'
  },
  sku_vip_monthly: {
    skuId: 'sku_vip_monthly',
    itemType: 'vip',
    name: '천하명인 패스',
    shortLabel: '명인 패스',
    quantity: 1,
    priceGems: 490,
    priceCoins: 0,
    badgeText: '일일 무료 한도 대폭 확대',
    description: '매일 무료 훈수 5회·상수 5회·방지권 3회·무르기 3회·천안통 3회·도전권 10장으로 자동 리필됩니다.',
    usageContext: '전 모드 상시 혜택'
  }
};

export function getCatalogSku(skuId: string): CatalogSkuSpec | null {
  return CATALOG_SKUS[skuId] || null;
}

export function listCatalogSkus(): CatalogSkuSpec[] {
  return Object.values(CATALOG_SKUS);
}

export interface PromotionSeriesState {
  active: boolean;
  type: 'promotion' | 'relegation';
  targetTierIndex: number;
  wins: number;
  losses: number;
  winsNeeded: number;
  gamesTotal: number;
}

export interface UserProfileState {
  userId: string;
  nickname: string;
  avatarId: string;
  tierIndex: number;          // 0 (18급) .. 26 (9단)
  rating: number;
  wins: number;
  losses: number;
  draws: number;
  winStreak: number;
  isSubscriber: boolean;      // 유료 정액제('천하명인 패스') 가입 여부
  gems: number;               // 보유 유료 캐시(보석)
  coins: number;              // 무료 대국 코인(엽전)
  ownedSangsu: number;        // 1. 총 보유 상수(象手) 개수 (daily + purchased + matchGrant 합계)
  ownedHunsu: number;         // 2. 총 보유 훈수(訓手) 개수
  ownedCrisisShields: number; // 3. 총 보유 위기감지 방지권 개수
  ownedUndoMoves: number;     // 4. 총 보유 한수 무르기 개수
  ownedEnemyIntent: number;   // 5. 총 보유 천안통 개수
  ownedRankShield: number;    // 6. 총 보유 점수 보호권 개수
  ownedExtend3Moves?: number; // 7. 총 보유 +3수 연장권 개수
  inventoryBuckets?: InventoryBuckets; // ★ 3-버킷 분리 원장 (dailyAllowance / purchasedInventory / matchGrant)
  migrationVersion?: number;           // ★ 멱등 마이그레이션 버전 (1)
  equippedBoardSkin: string;
  equippedPieceSkin: string;
  unlockedBoardSkins: string[];
  unlockedPieceSkins: string[];
  prestigeScore: number;
  upsetWins: number;
  puzzlesCleared: string[];
  challengeTickets: number;
  beautyStagesCleared: string[];
  hiredSecretaryIds: string[];
  equippedSecretaryId: string;
  equippedSecretaryPoseId?: 'pose1' | 'pose2' | 'pose3' | 'pose4' | 'pose5';
  dailyReviewUsed: number;
  lastRefillDate: string;     // YYYY-MM-DD (KST)
  promotionSeries: PromotionSeriesState | null;
}

export function createEmptyInventoryCounts(): InventoryCounts {
  return {
    hunsu: 0,
    sangsu: 0,
    shield: 0,
    vision: 0,
    undo: 0,
    rankedShield: 0,
    ticket: 0,
    extend3Moves: 0
  };
}

export function getDailyAllowanceTargets(isSubscriber: boolean): InventoryCounts {
  return {
    hunsu: isSubscriber ? ECONOMY_CONFIG.SUBSCRIPTION_DAILY_HUNSU_REFILL : ECONOMY_CONFIG.FREE_DAILY_HUNSU_REFILL,
    sangsu: isSubscriber ? ECONOMY_CONFIG.SUBSCRIPTION_DAILY_SANGSU_REFILL : ECONOMY_CONFIG.FREE_DAILY_SANGSU_REFILL,
    shield: isSubscriber ? ECONOMY_CONFIG.SUBSCRIPTION_DAILY_CRISIS_SHIELD_REFILL : ECONOMY_CONFIG.FREE_DAILY_CRISIS_SHIELD_REFILL,
    vision: isSubscriber ? ECONOMY_CONFIG.SUBSCRIPTION_DAILY_ENEMY_INTENT_REFILL : ECONOMY_CONFIG.FREE_DAILY_ENEMY_INTENT_REFILL,
    undo: isSubscriber ? ECONOMY_CONFIG.SUBSCRIPTION_DAILY_UNDO_REFILL : ECONOMY_CONFIG.FREE_DAILY_UNDO_REFILL,
    rankedShield: 0,
    ticket: isSubscriber ? ECONOMY_CONFIG.SUBSCRIPTION_DAILY_CHALLENGE_TICKETS : ECONOMY_CONFIG.FREE_DAILY_CHALLENGE_TICKETS,
    extend3Moves: 0
  };
}

// ============================================================================
// 3-버킷 동기화 헬퍼: flat 필드와 inventoryBuckets 간 일관성 보장
// ============================================================================
export function ensureInventoryBuckets(profile: UserProfileState): InventoryBuckets {
  const targets = getDailyAllowanceTargets(Boolean(profile.isSubscriber));
  if (!profile.inventoryBuckets) {
    // 초기 생성 또는 아직 버킷이 없는 경우: flat 수량을 dailyAllowance(상한까지) + purchasedInventory(초과분)로 안전 분리
    const split = (total: number | undefined, dailyCap: number): { daily: number; purchased: number } => {
      const safeTotal = Math.max(0, Math.floor(total ?? 0));
      const daily = Math.min(safeTotal, dailyCap);
      const purchased = Math.max(0, safeTotal - daily);
      return { daily, purchased };
    };
    const hunsuSplit = split(profile.ownedHunsu, targets.hunsu);
    const sangsuSplit = split(profile.ownedSangsu, targets.sangsu);
    const shieldSplit = split(profile.ownedCrisisShields, targets.shield);
    const visionSplit = split(profile.ownedEnemyIntent, targets.vision);
    const undoSplit = split(profile.ownedUndoMoves, targets.undo);
    const rankSplit = split(profile.ownedRankShield, targets.rankedShield);
    const ticketSplit = split(profile.challengeTickets, targets.ticket);
    const extendSplit = split(profile.ownedExtend3Moves, targets.extend3Moves);

    profile.inventoryBuckets = {
      dailyAllowance: {
        hunsu: hunsuSplit.daily,
        sangsu: sangsuSplit.daily,
        shield: shieldSplit.daily,
        vision: visionSplit.daily,
        undo: undoSplit.daily,
        rankedShield: rankSplit.daily,
        ticket: ticketSplit.daily,
        extend3Moves: extendSplit.daily
      },
      purchasedInventory: {
        hunsu: hunsuSplit.purchased,
        sangsu: sangsuSplit.purchased,
        shield: shieldSplit.purchased,
        vision: visionSplit.purchased,
        undo: undoSplit.purchased,
        rankedShield: rankSplit.purchased,
        ticket: ticketSplit.purchased,
        extend3Moves: extendSplit.purchased
      },
      matchGrant: createEmptyInventoryCounts()
    };
  }
  return profile.inventoryBuckets;
}

export function syncFlatInventoryFromBuckets(profile: UserProfileState): UserProfileState {
  const b = ensureInventoryBuckets(profile);
  const sum = (k: ConsumableItemType) =>
    Math.max(0, (b.dailyAllowance[k] || 0) + (b.purchasedInventory[k] || 0) + (b.matchGrant[k] || 0));

  profile.ownedHunsu = sum('hunsu');
  profile.ownedSangsu = sum('sangsu');
  profile.ownedCrisisShields = sum('shield');
  profile.ownedEnemyIntent = sum('vision');
  profile.ownedUndoMoves = sum('undo');
  profile.ownedRankShield = sum('rankedShield');
  profile.challengeTickets = sum('ticket');
  profile.ownedExtend3Moves = sum('extend3Moves');
  return profile;
}

/**
 * 만약 외부 코드(예: 구 테스트)가 profile.ownedRankShield = 1 처럼 flat 필드를 직접 수정했을 경우
 * rankedShield에 한해 버킷 합계와 flat 필드의 차이를 반영해 호환성을 유지한다.
 */
export function reconcileFlatToBuckets(profile: UserProfileState): UserProfileState {
  const b = ensureInventoryBuckets(profile);
  const key: ConsumableItemType = 'rankedShield';
  const flatVal = profile.ownedRankShield ?? 0;
  const bucketSum = (b.dailyAllowance[key] || 0) + (b.purchasedInventory[key] || 0) + (b.matchGrant[key] || 0);
  if (flatVal > bucketSum) {
    b.purchasedInventory[key] = (b.purchasedInventory[key] || 0) + (flatVal - bucketSum);
  } else if (flatVal < bucketSum) {
    let diff = bucketSum - Math.max(0, flatVal);
    for (const bucketName of ['matchGrant', 'dailyAllowance', 'purchasedInventory'] as InventoryBucketName[]) {
      const avail = b[bucketName][key] || 0;
      const take = Math.min(avail, diff);
      b[bucketName][key] = avail - take;
      diff -= take;
    }
  }
  return syncFlatInventoryFromBuckets(profile);
}

export function getBucketTotal(profile: UserProfileState, itemType: ConsumableItemType): number {
  if (itemType === 'rankedShield') reconcileFlatToBuckets(profile);
  const b = ensureInventoryBuckets(profile);
  return (b.matchGrant[itemType] || 0) + (b.dailyAllowance[itemType] || 0) + (b.purchasedInventory[itemType] || 0);
}

export function grantMatchHandicapItems(
  profile: UserProfileState,
  grants: Partial<InventoryCounts>
): UserProfileState {
  const b = ensureInventoryBuckets(profile);
  b.matchGrant = createEmptyInventoryCounts();
  for (const [k, v] of Object.entries(grants)) {
    const key = k as ConsumableItemType;
    if (typeof v === 'number' && v > 0) {
      b.matchGrant[key] = Math.floor(v);
    }
  }
  return syncFlatInventoryFromBuckets(profile);
}

export function clearMatchGrantItems(profile: UserProfileState): UserProfileState {
  const b = ensureInventoryBuckets(profile);
  b.matchGrant = createEmptyInventoryCounts();
  return syncFlatInventoryFromBuckets(profile);
}

/**
 * 3-버킷 차감 우선순위 (A12): matchGrant -> dailyAllowance -> purchasedInventory
 */
export function consumeInventoryItem(
  profile: UserProfileState,
  itemType: ConsumableItemType,
  amount = 1
): { ok: boolean; reason?: string; receipt?: InventoryDeductionReceipt; remaining: number } {
  if (itemType === 'rankedShield') reconcileFlatToBuckets(profile);
  const b = ensureInventoryBuckets(profile);
  const totalAvailable = getBucketTotal(profile, itemType);
  if (amount <= 0) {
    return { ok: true, remaining: totalAvailable };
  }
  if (totalAvailable < amount) {
    return { ok: false, reason: 'insufficient_item', remaining: totalAvailable };
  }

  let needed = amount;
  const breakdown: Array<{ bucket: InventoryBucketName; amount: number }> = [];
  const order: InventoryBucketName[] = ['matchGrant', 'dailyAllowance', 'purchasedInventory'];
  const bucketAmounts: Record<InventoryBucketName, number> = {
    matchGrant: 0,
    dailyAllowance: 0,
    purchasedInventory: 0
  };

  for (const bucketName of order) {
    if (needed <= 0) break;
    const avail = b[bucketName][itemType] || 0;
    if (avail > 0) {
      const deduct = Math.min(avail, needed);
      b[bucketName][itemType] = avail - deduct;
      needed -= deduct;
      bucketAmounts[bucketName] = deduct;
      breakdown.push({ bucket: bucketName, amount: deduct });
    }
  }

  syncFlatInventoryFromBuckets(profile);
  const receipt: InventoryDeductionReceipt = {
    receiptId: `rcpt_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
    itemType,
    amount,
    breakdown,
    fromMatchGrant: bucketAmounts.matchGrant,
    fromDailyAllowance: bucketAmounts.dailyAllowance,
    fromPurchasedInventory: bucketAmounts.purchasedInventory,
    timestamp: Date.now()
  };
  return {
    ok: true,
    receipt,
    remaining: getBucketTotal(profile, itemType)
  };
}

export function refundInventoryItem(
  profile: UserProfileState,
  receipt: InventoryDeductionReceipt
): UserProfileState {
  const b = ensureInventoryBuckets(profile);
  for (const entry of receipt.breakdown) {
    b[entry.bucket][receipt.itemType] = (b[entry.bucket][receipt.itemType] || 0) + entry.amount;
  }
  return syncFlatInventoryFromBuckets(profile);
}

/**
 * 통합 카탈로그 SKU 구매 처리 (A13, A14)
 * - 잔액 부족 시 절대 자동으로 +500 보석을 리필하지 않으며 명시적 insufficient_gems 반환
 * - 구매한 수량은 반드시 purchasedInventory 버킷에 적립되어 일일 리셋 시 보존됨
 */
export function purchaseCatalogSku(
  profile: UserProfileState,
  skuId: string
): {
  ok: boolean;
  reason?: 'invalid_sku' | 'insufficient_gems';
  sku?: CatalogSkuSpec;
  updatedProfile: UserProfileState;
} {
  const sku = getCatalogSku(skuId);
  if (!sku) {
    return { ok: false, reason: 'invalid_sku', updatedProfile: profile };
  }
  const p: UserProfileState = JSON.parse(JSON.stringify(profile));
  reconcileFlatToBuckets(p);

  if ((p.gems ?? 0) < sku.priceGems) {
    return { ok: false, reason: 'insufficient_gems', sku, updatedProfile: p };
  }

  p.gems -= sku.priceGems;
  if (sku.itemType === 'vip') {
    const activated = activateSubscription(p);
    return { ok: true, sku, updatedProfile: activated };
  }

  const b = ensureInventoryBuckets(p);
  b.purchasedInventory[sku.itemType] = (b.purchasedInventory[sku.itemType] || 0) + sku.quantity;
  syncFlatInventoryFromBuckets(p);

  return { ok: true, sku, updatedProfile: p };
}

export function createDefaultUserProfile(nickname = '종로대국수'): UserProfileState {
  const initialTier = getTierByLabel('9급');
  const today = getKstDateString();
  const dailyTargets = getDailyAllowanceTargets(false);
  const purchasedInit: InventoryCounts = {
    hunsu: 0,
    sangsu: 0,
    shield: 1,       // 기본 무료 1 + 초기 보너스 1 = 총 2개
    vision: 1,       // 기본 무료 1 + 초기 보너스 1 = 총 2개
    undo: 1,         // 기본 무료 1 + 초기 보너스 1 = 총 2개
    rankedShield: 1, // 기본 보유 보호권 1개
    ticket: 0,
    extend3Moves: 0
  };

  const profile: UserProfileState = {
    userId: 'user_' + Math.random().toString(36).slice(2, 9),
    nickname,
    avatarId: 'avatar_05',
    tierIndex: initialTier.index,
    rating: initialTier.baseRating + 40,
    wins: 14,
    losses: 9,
    draws: 1,
    winStreak: 2,
    isSubscriber: false,
    gems: 2400,
    coins: 15000,
    ownedSangsu: dailyTargets.sangsu + purchasedInit.sangsu,
    ownedHunsu: dailyTargets.hunsu + purchasedInit.hunsu,
    ownedCrisisShields: dailyTargets.shield + purchasedInit.shield,
    ownedUndoMoves: dailyTargets.undo + purchasedInit.undo,
    ownedEnemyIntent: dailyTargets.vision + purchasedInit.vision,
    ownedRankShield: dailyTargets.rankedShield + purchasedInit.rankedShield,
    ownedExtend3Moves: 0,
    inventoryBuckets: {
      dailyAllowance: { ...dailyTargets },
      purchasedInventory: { ...purchasedInit },
      matchGrant: createEmptyInventoryCounts()
    },
    migrationVersion: 1,
    equippedBoardSkin: 'board_zelkova',
    equippedPieceSkin: 'piece_boxwood_master',
    unlockedBoardSkins: ['board_zelkova', 'board_royal_pine'],
    unlockedPieceSkins: ['piece_jujube_classic', 'piece_boxwood_master'],
    prestigeScore: 860,
    upsetWins: 3,
    puzzlesCleared: [],
    challengeTickets: dailyTargets.ticket + purchasedInit.ticket,
    beautyStagesCleared: ['ch_01_st_1'],
    hiredSecretaryIds: [],
    equippedSecretaryId: 'ch_01',
    equippedSecretaryPoseId: 'pose1',
    dailyReviewUsed: 0,
    lastRefillDate: today,
    promotionSeries: null
  };
  return syncFlatInventoryFromBuckets(profile);
}

// ============================================================================
// 구버전 프로필 멱등 마이그레이션 (migrateUserProfile - A17)
// - 기존 유저의 스테이지 클리어, 화보 해금, 스킨, 재화, 아이템 수량을 100% 보존하면서
//   3-버킷 구조(dailyAllowance / purchasedInventory / matchGrant)로 안전하게 이관
// - 두 번 이상 실행해도 수량이 중복 증가하지 않음 (migrationVersion === 1)
// ============================================================================
export function migrateUserProfile(rawProfile: Partial<UserProfileState> | null | undefined): UserProfileState {
  const fallback = createDefaultUserProfile();
  if (!rawProfile || typeof rawProfile !== 'object') {
    return fallback;
  }

  if (rawProfile.migrationVersion === 1 && rawProfile.inventoryBuckets) {
    const cloned: UserProfileState = {
      ...fallback,
      ...JSON.parse(JSON.stringify(rawProfile))
    };
    return syncFlatInventoryFromBuckets(cloned);
  }

  const isSub = Boolean(rawProfile.isSubscriber);
  const targets = getDailyAllowanceTargets(isSub);

  const splitLegacyCount = (val: number | undefined, fallbackVal: number, dailyCap: number) => {
    const safe = typeof val === 'number' && !Number.isNaN(val) ? Math.max(0, Math.floor(val)) : fallbackVal;
    const daily = Math.min(safe, dailyCap);
    const purchased = Math.max(0, safe - daily);
    return { daily, purchased };
  };

  const hunsu = splitLegacyCount(rawProfile.ownedHunsu, fallback.ownedHunsu, targets.hunsu);
  const sangsu = splitLegacyCount(rawProfile.ownedSangsu, fallback.ownedSangsu, targets.sangsu);
  const shield = splitLegacyCount(rawProfile.ownedCrisisShields, fallback.ownedCrisisShields, targets.shield);
  const vision = splitLegacyCount(rawProfile.ownedEnemyIntent, fallback.ownedEnemyIntent, targets.vision);
  const undo = splitLegacyCount(rawProfile.ownedUndoMoves, fallback.ownedUndoMoves, targets.undo);
  const rankShield = splitLegacyCount(rawProfile.ownedRankShield, fallback.ownedRankShield, targets.rankedShield);
  const ticket = splitLegacyCount(rawProfile.challengeTickets, fallback.challengeTickets, targets.ticket);
  const extend = splitLegacyCount(rawProfile.ownedExtend3Moves, 0, 0);

  const clearedStages = Array.isArray(rawProfile.beautyStagesCleared)
    ? Array.from(new Set(rawProfile.beautyStagesCleared))
    : [...fallback.beautyStagesCleared];

  const hiredIds = Array.isArray(rawProfile.hiredSecretaryIds)
    ? Array.from(new Set(rawProfile.hiredSecretaryIds))
    : [];

  const migrated: UserProfileState = {
    ...fallback,
    ...rawProfile,
    isSubscriber: isSub,
    gems: typeof rawProfile.gems === 'number' ? rawProfile.gems : fallback.gems,
    coins: typeof rawProfile.coins === 'number' ? rawProfile.coins : fallback.coins,
    unlockedBoardSkins: Array.isArray(rawProfile.unlockedBoardSkins) && rawProfile.unlockedBoardSkins.length > 0
      ? Array.from(new Set(rawProfile.unlockedBoardSkins))
      : [...fallback.unlockedBoardSkins],
    unlockedPieceSkins: Array.isArray(rawProfile.unlockedPieceSkins) && rawProfile.unlockedPieceSkins.length > 0
      ? Array.from(new Set(rawProfile.unlockedPieceSkins))
      : [...fallback.unlockedPieceSkins],
    puzzlesCleared: Array.isArray(rawProfile.puzzlesCleared)
      ? Array.from(new Set(rawProfile.puzzlesCleared))
      : [],
    beautyStagesCleared: clearedStages,
    hiredSecretaryIds: hiredIds,
    equippedSecretaryId: rawProfile.equippedSecretaryId || 'ch_01',
    equippedSecretaryPoseId: rawProfile.equippedSecretaryPoseId || 'pose1',
    inventoryBuckets: {
      dailyAllowance: {
        hunsu: hunsu.daily,
        sangsu: sangsu.daily,
        shield: shield.daily,
        vision: vision.daily,
        undo: undo.daily,
        rankedShield: rankShield.daily,
        ticket: ticket.daily,
        extend3Moves: extend.daily
      },
      purchasedInventory: {
        hunsu: hunsu.purchased,
        sangsu: sangsu.purchased,
        shield: shield.purchased,
        vision: vision.purchased,
        undo: undo.purchased,
        rankedShield: rankShield.purchased,
        ticket: ticket.purchased,
        extend3Moves: extend.purchased
      },
      matchGrant: createEmptyInventoryCounts()
    },
    migrationVersion: 1
  };

  return syncFlatInventoryFromBuckets(migrated);
}

// ============================================================================
// 일일 리필 처리 (A11: dailyAllowance만 갱신하고 purchasedInventory는 절대 덮어쓰지 않음!)
// ============================================================================
export function applyDailyRefill(
  profile: UserProfileState,
  forceRefill = false,
  nowDate: Date = new Date()
): UserProfileState {
  const today = getKstDateString(nowDate);
  const p: UserProfileState = JSON.parse(JSON.stringify(profile));
  reconcileFlatToBuckets(p);

  if (p.lastRefillDate === today && !forceRefill) {
    return syncFlatInventoryFromBuckets(p);
  }

  const targets = getDailyAllowanceTargets(Boolean(p.isSubscriber));
  const b = ensureInventoryBuckets(p);

  // dailyAllowance만 일일 목표치로 리필하고, purchasedInventory는 그대로 유지!
  b.dailyAllowance.hunsu = Math.max(b.dailyAllowance.hunsu || 0, targets.hunsu);
  b.dailyAllowance.sangsu = Math.max(b.dailyAllowance.sangsu || 0, targets.sangsu);
  b.dailyAllowance.shield = Math.max(b.dailyAllowance.shield || 0, targets.shield);
  b.dailyAllowance.vision = Math.max(b.dailyAllowance.vision || 0, targets.vision);
  b.dailyAllowance.undo = Math.max(b.dailyAllowance.undo || 0, targets.undo);
  b.dailyAllowance.ticket = Math.max(b.dailyAllowance.ticket || 0, targets.ticket);

  p.beautyStagesCleared = p.beautyStagesCleared ?? ['ch_01_st_1'];
  p.hiredSecretaryIds = p.hiredSecretaryIds ?? [];
  p.equippedSecretaryId = p.equippedSecretaryId || 'ch_01';
  p.dailyReviewUsed = 0;
  p.lastRefillDate = today;

  return syncFlatInventoryFromBuckets(p);
}

// 정액제 가입 처리 (일일 무료 버킷을 정액제 한도로 즉시 상향 + 가입 보너스를 purchasedInventory에 적립)
export function activateSubscription(profile: UserProfileState): UserProfileState {
  const p: UserProfileState = JSON.parse(JSON.stringify(profile));
  reconcileFlatToBuckets(p);
  p.isSubscriber = true;

  const subTargets = getDailyAllowanceTargets(true);
  const b = ensureInventoryBuckets(p);

  b.dailyAllowance.sangsu = Math.max(b.dailyAllowance.sangsu, subTargets.sangsu);
  b.dailyAllowance.hunsu = Math.max(b.dailyAllowance.hunsu, subTargets.hunsu);
  b.dailyAllowance.shield = Math.max(b.dailyAllowance.shield, subTargets.shield);
  b.dailyAllowance.undo = Math.max(b.dailyAllowance.undo, subTargets.undo);
  b.dailyAllowance.vision = Math.max(b.dailyAllowance.vision, subTargets.vision);
  b.dailyAllowance.ticket = Math.max(b.dailyAllowance.ticket, subTargets.ticket);

  // 정액제 가입 즉시 보너스는 구매 버킷(purchasedInventory)에 추가하여 영구 보존
  b.purchasedInventory.rankedShield = (b.purchasedInventory.rankedShield || 0) + 2;

  return syncFlatInventoryFromBuckets(p);
}

// 비서 동행 보너스 비율 계산 (챕터 1: +10% ~ 챕터 20: +100%)
export function getSecretaryCoinBonusPercent(chapterId: string): number {
  const match = /^ch_(\d+)$/.exec(chapterId || 'ch_01');
  const chNum = match ? Math.max(1, Math.min(20, parseInt(match[1], 10))) : 1;
  const table: Record<number, number> = {
    1: 10, 2: 12, 3: 15, 4: 18, 5: 20,
    6: 22, 7: 25, 8: 28, 9: 30, 10: 35,
    11: 38, 12: 40, 13: 42, 14: 45, 15: 48,
    16: 52, 17: 55, 18: 60, 19: 70, 20: 100
  };
  return table[chNum] ?? 10;
}

// 대국 결과에 따른 레이팅 및 18급~9단 승강제 업데이트
export function applyMatchResultToProfile(
  profile: UserProfileState,
  result: 'win' | 'loss' | 'draw',
  opponentTierIndex: number
): { updated: UserProfileState; eventBanner: string | null } {
  const p: UserProfileState = JSON.parse(JSON.stringify(profile));
  reconcileFlatToBuckets(p);
  p.upsetWins = p.upsetWins ?? 0;

  const currentTier = getTierByIndex(p.tierIndex);
  const nextTierBoundary = currentTier.baseRating + 75;
  const prevTierBoundary = currentTier.baseRating;
  const tierDiff = opponentTierIndex - p.tierIndex;

  let eventBanner: string | null = null;

  if (result === 'draw') {
    p.draws += 1;
    return { updated: syncFlatInventoryFromBuckets(p), eventBanner: '무승부로 대국이 종료되었습니다.' };
  }

  if (result === 'win') {
    p.wins += 1;
    p.winStreak += 1;
    const bonusPct = getSecretaryCoinBonusPercent(p.equippedSecretaryId);
    const baseCoinReward = 250 + (tierDiff > 0 ? tierDiff * 150 : 0);
    p.coins += Math.round(baseCoinReward * (1 + bonusPct / 100));
    if (tierDiff > 0) {
      p.upsetWins += 1;
    }
  } else {
    p.losses += 1;
    if (getBucketTotal(p, 'rankedShield') <= 0) {
      p.winStreak = 0;
    }
  }

  // 이미 승급전 또는 강등방어전 진행 중인 경우
  if (p.promotionSeries && p.promotionSeries.active) {
    if (result === 'win') {
      p.promotionSeries.wins += 1;
    } else if (getBucketTotal(p, 'rankedShield') > 0) {
      consumeInventoryItem(p, 'rankedShield', 1);
      eventBanner = `📜 [어전 강등방어 면책패] 발동! 승급/방어전 패배가 무효 처리되었습니다! (남은 면책패: ${p.ownedRankShield}개)`;
      return { updated: syncFlatInventoryFromBuckets(p), eventBanner };
    } else {
      p.promotionSeries.losses += 1;
    }

    const lossesAllowed = p.promotionSeries.gamesTotal - p.promotionSeries.winsNeeded;
    if (p.promotionSeries.wins >= p.promotionSeries.winsNeeded) {
      if (p.promotionSeries.type === 'promotion') {
        p.tierIndex = Math.min(ALL_TIERS.length - 1, p.promotionSeries.targetTierIndex);
        const promotedTier = getTierByIndex(p.tierIndex);
        p.rating = promotedTier.baseRating + 35;
        const b = ensureInventoryBuckets(p);
        b.purchasedInventory.hunsu += 2;
        b.purchasedInventory.vision += 1;
        syncFlatInventoryFromBuckets(p);
        eventBanner = `🎉 축하합니다! 승급전을 돌파하여 [${promotedTier.label} (${promotedTier.shortTitle})]으로 승급하셨습니다! (보상: 훈수 +2, 천안통 +1)`;
      } else {
        p.rating = currentTier.baseRating + 35;
        eventBanner = `🛡️ 강등 방어전 승리! [${currentTier.label}] 티어를 굳건히 지켜냈습니다!`;
      }
      p.promotionSeries = null;
    } else if (p.promotionSeries.losses > lossesAllowed) {
      if (p.promotionSeries.type === 'promotion') {
        p.rating = nextTierBoundary - 25;
        eventBanner = `아쉽게도 승급전에 실패했습니다. 재정비 후 다시 도전하세요!`;
      } else {
        p.tierIndex = Math.max(0, p.promotionSeries.targetTierIndex);
        const relegatedTier = getTierByIndex(p.tierIndex);
        p.rating = relegatedTier.baseRating + 45;
        eventBanner = `⚠️ 강등 방어전 패배로 [${relegatedTier.label}]으로 강등되었습니다.`;
      }
      p.promotionSeries = null;
    } else {
      const seriesLabel = p.promotionSeries.type === 'promotion' ? '승급전' : '강등방어전';
      eventBanner = `🔥 현재 ${seriesLabel} 진행 중 (${p.promotionSeries.wins}승 ${p.promotionSeries.losses}패 / 목표 ${p.promotionSeries.winsNeeded}승)`;
    }
    return { updated: syncFlatInventoryFromBuckets(p), eventBanner };
  }

  // 일반 레이팅 변동
  let delta = 0;
  if (result === 'win') {
    const baseWinDelta = Math.max(15, Math.min(45, 28 + tierDiff * 4));
    if (tierDiff > 0 && getBucketTotal(p, 'rankedShield') > 0) {
      consumeInventoryItem(p, 'rankedShield', 1);
      delta = baseWinDelta * 2;
      eventBanner = `📜 [고수격파 승점 2배 부적] 발동! 상위 고수 제압으로 승점 +${delta}점 폭풍 획득!`;
    } else {
      delta = baseWinDelta;
    }
  } else {
    if (getBucketTotal(p, 'rankedShield') > 0) {
      consumeInventoryItem(p, 'rankedShield', 1);
      delta = 0;
      eventBanner = `📜 [어전 강등방어 면책패] 발동! 패배로 인한 급·단수 점수 하락과 연승 초기화가 완벽 방어되었습니다!`;
    } else {
      delta = -Math.max(12, Math.min(38, 24 - tierDiff * 3));
    }
  }

  p.rating += delta;

  if (p.rating >= nextTierBoundary && p.tierIndex < ALL_TIERS.length - 1) {
    p.rating = nextTierBoundary - 1;
    const nextTier = getTierByIndex(p.tierIndex + 1);
    p.promotionSeries = {
      active: true,
      type: 'promotion',
      targetTierIndex: p.tierIndex + 1,
      wins: 0,
      losses: 0,
      winsNeeded: currentTier.promotionWinsNeeded,
      gamesTotal: currentTier.promotionGamesTotal
    };
    eventBanner = `⚔️ [${nextTier.label}] 승급전 돌입! (${currentTier.promotionGamesTotal}판 중 ${currentTier.promotionWinsNeeded}승 달성 시 승급)`;
  } else if (p.rating < prevTierBoundary && p.tierIndex > 0) {
    p.rating = prevTierBoundary;
    p.promotionSeries = {
      active: true,
      type: 'relegation',
      targetTierIndex: p.tierIndex - 1,
      wins: 0,
      losses: 0,
      winsNeeded: 2,
      gamesTotal: 3
    };
    eventBanner = `🛡️ 강등 위기! 강등 방어전(3판 2승)에 돌입합니다.`;
  }

  return { updated: syncFlatInventoryFromBuckets(p), eventBanner };
}
